import type { FC, HTMLAttributes, ReactNode } from 'react'
import styles from './alert.module.css'

export type AlertSeverity = 'error' | 'warning' | 'info' | 'success'

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  severity?: AlertSeverity
  variant?: 'standard' | 'filled' | 'outlined'
  icon?: ReactNode | false
  action?: ReactNode
  onClose?: () => void
}

const variantSeverityMap: Record<string, Record<string, string | undefined>> = {
  filled: {
    error: styles.filledError,
    warning: styles.filledWarning,
    info: styles.filledInfo,
    success: styles.filledSuccess,
  },
  standard: {
    error: styles.standardError,
    warning: styles.standardWarning,
    info: styles.standardInfo,
    success: styles.standardSuccess,
  },
  outlined: {
    error: styles.outlinedError,
    warning: styles.outlinedWarning,
    info: styles.outlinedInfo,
    success: styles.outlinedSuccess,
  },
}

export const Alert: FC<AlertProps> = ({
  severity = 'success',
  variant = 'standard',
  icon,
  action,
  onClose,
  className,
  children,
  ...rest
}) => {
  const classes = [
    styles.root,
    variant === 'outlined' ? styles.outlined : undefined,
    variantSeverityMap[variant]?.[severity],
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes} {...rest} role="alert">
      {icon !== false ? <span className={styles.icon}>{icon}</span> : null}
      <div style={{ flex: 1 }}>{children}</div>
      {action}
      {onClose ? (
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
        >
          {'\u00d7'}
        </button>
      ) : null}
    </div>
  )
}

export interface AlertTitleProps extends HTMLAttributes<HTMLDivElement> {}

export const AlertTitle: FC<AlertTitleProps> = ({ className, ...rest }) => (
  <div className={[styles.title, className].filter(Boolean).join(' ')} {...rest} />
)
